import type {
  HrPermissionCode,
  PermissionCheckMode,
  PermissionsByScope,
} from '@/modules/Core/constants/hrPermissions'

export function buildPermissionCode(
  scope: string,
  name: string
): HrPermissionCode | string {
  return `${scope}.${name}`
}

export function parsePermissions(
  map: PermissionsByScope | null | undefined
): HrPermissionCode[] {
  if (!map) {
    return []
  }

  const codes = new Set<HrPermissionCode>()

  for (const [scope, entries] of Object.entries(map)) {
    if (!entries?.length) {
      continue
    }

    for (const entry of entries) {
      if (!entry?.name) {
        continue
      }

      codes.add(buildPermissionCode(scope, entry.name) as HrPermissionCode)
    }
  }

  return [...codes]
}

export function toPermissionSet(
  permissions: readonly HrPermissionCode[] | null | undefined
): ReadonlySet<HrPermissionCode> {
  return new Set(permissions ?? [])
}

export function hasPermission(
  userPermissions: ReadonlySet<HrPermissionCode>,
  code: HrPermissionCode
): boolean {
  return userPermissions.has(code)
}

export function hasAnyPermission(
  userPermissions: ReadonlySet<HrPermissionCode>,
  codes: readonly HrPermissionCode[]
): boolean {
  if (codes.length === 0) {
    return true
  }

  return codes.some((code) => userPermissions.has(code))
}

export function hasAllPermissions(
  userPermissions: ReadonlySet<HrPermissionCode>,
  codes: readonly HrPermissionCode[]
): boolean {
  if (codes.length === 0) {
    return true
  }

  return codes.every((code) => userPermissions.has(code))
}

export function canAccessPermissions(
  userPermissions: readonly HrPermissionCode[] | ReadonlySet<HrPermissionCode>,
  required: readonly HrPermissionCode[],
  mode: PermissionCheckMode = 'any'
): boolean {
  if (required.length === 0) {
    return true
  }

  const set =
    userPermissions instanceof Set
      ? userPermissions
      : toPermissionSet(userPermissions)

  if (set.size === 0) {
    return false
  }

  return mode === 'all'
    ? hasAllPermissions(set, required)
    : hasAnyPermission(set, required)
}
